import { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Filter, X } from 'lucide-react';

export type SortDir = 'asc' | 'desc' | null;

interface BaseProps {
  label: string;
  sortKey?: string;
  sortBy?: string | null;
  sortDir?: SortDir;
  onSort?: (key: string, dir: SortDir) => void;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

interface CategoricalFilter extends BaseProps {
  filterType?: 'categorical';
  // Acepta string[] (label==value) o {value,label}[] (label visible distinto del value enviado al backend)
  filterOptions?: Array<string | { value: string; label: string }>;
  filterValue?: string;
  onFilter?: (value: string) => void;
}

interface NumericFilter extends BaseProps {
  filterType: 'numeric';
  filterMin?: number | '';
  filterMax?: number | '';
  onFilterRange?: (min: number | '', max: number | '') => void;
  filterValue?: undefined;
  onFilter?: undefined;
  filterOptions?: undefined;
}

type Props = CategoricalFilter | NumericFilter;

/**
 * Header de tabla con ordenamiento ASC/DESC + filtro por columna.
 * - Click en el label → toggle sort (none → asc → desc → none)
 * - Click en ícono filtro → popover con opciones (categórico) o min/max (numérico)
 */
export function SortableTh(props: Props) {
  const {
    label, sortKey, sortBy, sortDir, onSort,
    align = 'left', className = '',
  } = props;

  const [showFilter, setShowFilter] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const sortable = !!(sortKey && onSort);
  const isActive = sortable && sortBy === sortKey;

  const toggleSort = () => {
    if (!sortable || !onSort) return;
    if (!isActive) onSort(sortKey!, 'asc');
    else if (sortDir === 'asc') onSort(sortKey!, 'desc');
    else onSort(sortKey!, null);
  };

  useEffect(() => {
    if (!showFilter) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setShowFilter(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFilter]);

  const isCategorical = props.filterType !== 'numeric';
  const filterActive = isCategorical
    ? !!props.filterValue
    : (props.filterMin !== '' && props.filterMin !== undefined) ||
      (props.filterMax !== '' && props.filterMax !== undefined);

  const filterable =
    (isCategorical && props.filterOptions && props.onFilter) ||
    (!isCategorical && props.onFilterRange);

  return (
    <th className={`px-2 py-2 select-none whitespace-nowrap text-${align} ${className}`}>
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}>
        {sortable ? (
          <button
            type="button"
            onClick={toggleSort}
            className="flex items-center gap-1 hover:text-arpp-primary group"
            title="Ordenar"
          >
            <span className={isActive ? 'font-bold text-arpp-primary' : ''}>{label}</span>
            {isActive && sortDir === 'asc' && <ChevronUp size={12} className="text-arpp-primary" />}
            {isActive && sortDir === 'desc' && <ChevronDown size={12} className="text-arpp-primary" />}
            {!isActive && <ChevronsUpDown size={11} className="text-gray-400 group-hover:text-arpp-primary" />}
          </button>
        ) : (
          <span>{label}</span>
        )}
        {filterable && (
          <div className="relative" ref={popRef}>
            <button
              type="button"
              onClick={() => setShowFilter((v) => !v)}
              className={`p-0.5 rounded hover:bg-gray-200 ${filterActive ? 'text-arpp-primary' : 'text-gray-400'}`}
              title="Filtrar columna"
            >
              <Filter size={11} fill={filterActive ? 'currentColor' : 'none'} />
            </button>
            {showFilter && (
              <div className="absolute right-0 top-5 z-30 bg-white border shadow-lg rounded p-2 min-w-[180px] text-xs font-normal text-gray-700">
                {isCategorical ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Filtrar {label.toLowerCase()}</span>
                      {props.filterValue && (
                        <button
                          onClick={() => {
                            props.onFilter!('');
                            setShowFilter(false);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Limpiar"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                    <select
                      value={props.filterValue ?? ''}
                      onChange={(e) => {
                        props.onFilter!(e.target.value);
                        setShowFilter(false);
                      }}
                      className="w-full border rounded px-2 py-1 text-xs"
                    >
                      <option value="">— Todas —</option>
                      {props.filterOptions!.map((o) => {
                        const v = typeof o === 'string' ? o : o.value;
                        const lbl = typeof o === 'string' ? o : o.label;
                        return <option key={v} value={v}>{lbl}</option>;
                      })}
                    </select>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Rango {label.toLowerCase()}</span>
                      {filterActive && (
                        <button
                          onClick={() => {
                            props.onFilterRange!('', '');
                            setShowFilter(false);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Limpiar"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1 items-center">
                      <input
                        type="number"
                        value={props.filterMin ?? ''}
                        onChange={(e) => props.onFilterRange!(e.target.value === '' ? '' : Number(e.target.value), props.filterMax ?? '')}
                        placeholder="min"
                        className="w-16 border rounded px-1 py-0.5 text-xs"
                      />
                      <span className="text-gray-400">–</span>
                      <input
                        type="number"
                        value={props.filterMax ?? ''}
                        onChange={(e) => props.onFilterRange!(props.filterMin ?? '', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="max"
                        className="w-16 border rounded px-1 py-0.5 text-xs"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </th>
  );
}
